import { useId, useState } from 'react';
import { usePublicConfig } from '@/hooks/api/usePublicConfig';
import { InfoIcon } from '@/components/common/InfoIcon';
import { t } from '@/i18n';
import styles from './MobileSpreadInfo.module.css';

const tips = [
  {
    icon: String.fromCodePoint(0x1f4f7),
    lead: t.spread.aside.tips.photo.lead,
    body: t.spread.aside.tips.photo.body,
  },
  {
    icon: String.fromCodePoint(0x1f4ac),
    lead: t.spread.aside.tips.question.lead,
    body: t.spread.aside.tips.question.body,
  },
  {
    icon: String.fromCodePoint(0x1f552),
    lead: t.spread.aside.tips.time.lead,
    body: t.spread.aside.tips.time.body,
  },
] as const;

export function MobileSpreadInfo() {
  const [isOpen, setIsOpen] = useState(false);
  const contentId = useId();
  const { data: config } = usePublicConfig();
  const telegramUsername = config?.supportTgUsername.trim().replace(/^@/, '');
  const telegramUrl = telegramUsername
    ? `https://t.me/${telegramUsername}`
    : undefined;
  const emailUrl = `mailto:${t.profile.support.emailAddress}`;

  return (
    <section
      className={[styles.panel, isOpen ? styles.open : '']
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className={styles.titleRow}>
          <InfoIcon />
          <span className={styles.title}>{t.spread.aside.title}</span>
        </span>
        <span className={styles.toggle} aria-hidden />
      </button>

      <div
        id={contentId}
        className={styles.contentGrid}
        aria-hidden={!isOpen}
        inert={!isOpen}
      >
        <div className={styles.contentClip}>
          <div className={styles.content}>
            <p className={styles.intro}>{t.spread.aside.intro}</p>
            <ul className={styles.cards}>
              {tips.map((tip) => (
                <li key={tip.lead} className={styles.card}>
                  <span className={styles.cardIco} aria-hidden>
                    {tip.icon}
                  </span>
                  <p className={styles.cardText}>
                    <strong>{tip.lead}</strong>
                    {tip.body}
                  </p>
                </li>
              ))}
            </ul>

            <div className={styles.support}>
              <div className={styles.supportTitle}>
                {t.profile.support.prompt}
              </div>
              <div className={styles.supportActions}>
                <a
                  className={[
                    styles.supportButton,
                    !telegramUrl ? styles.supportButtonDisabled : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  href={telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t.profile.support.telegram}
                  aria-disabled={!telegramUrl}
                >
                  <span className={styles.supportIcon} aria-hidden>
                    {String.fromCodePoint(0x2708, 0xfe0f)}
                  </span>
                  Telegram
                </a>
                <a
                  className={styles.supportButton}
                  href={emailUrl}
                  aria-label={t.profile.support.email}
                >
                  <span className={styles.supportIcon} aria-hidden>
                    {String.fromCodePoint(0x2709, 0xfe0f)}
                  </span>
                  Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
